import { createFileRoute } from '@tanstack/react-router';
import { Boxes, Building2, Users, Award, Check, ArrowLeft, ArrowRight } from 'lucide-react';
import { ComponentTabs } from '@/components/glitch-ui/component-tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { useState } from 'react';

export const Route = createFileRoute('/_authenticated/_layout/components/wizards')({
  component: WizardsPage,
  staticData: {
    title: 'Components',
    icon: Boxes,
  },
});

function WizardsPage() {
  const [selectedTier, setSelectedTier] = useState<string>('');

  return (
    <div className="flex flex-col h-full">
      <ComponentTabs />

      <div className="flex-1 pb-8">
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Wizards</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Business Card */}
            <label className="cursor-pointer block">
              <input
                type="radio"
                name="tier-selection"
                value="business"
                className="sr-only peer"
                onChange={(e) => setSelectedTier(e.target.value)}
              />
              <motion.div
                animate={selectedTier === 'business' ? { scale: 1.05 } : { scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 10,
                  duration: 0.3
                }}
              >
                <div
                  className="relative rounded-xl p-12 border-2 transition-all duration-300 ease-in-out min-h-[280px] flex flex-col items-center justify-center text-center"
                  style={{
                    background: 'linear-gradient(215deg, #F7F7F7 35%, #D6CDEE 100%)',
                    borderColor: selectedTier === 'business' ? '#290BD9' : 'transparent'
                  }}
                >
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                    <Building2 className="h-7 w-7" style={{ color: '#9B8FAD' }} />
                  </div>
                  <h3 className="text-2xl font-medium mb-2" style={{ color: '#4A3D56' }}>Business</h3>
                  <p className="text-lg" style={{ color: '#5A4D66' }}>Perfect for local businesses and startups</p>
                  <div className="absolute top-6 right-6 w-7 h-7 rounded-full bg-white border-2 flex items-center justify-center transition-all duration-300" style={{ borderColor: selectedTier === 'business' ? '#10B981' : '#E5E7EB' }}>
                    <Check
                      className="h-5 w-5 transition-opacity duration-200"
                      strokeWidth={3}
                      style={{
                        color: '#10B981',
                        opacity: selectedTier === 'business' ? 1 : 0
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            </label>

            {/* Agency Card */}
            <label className="cursor-pointer block">
              <input
                type="radio"
                name="tier-selection"
                value="agency"
                className="sr-only peer"
                onChange={(e) => setSelectedTier(e.target.value)}
              />
              <motion.div
                animate={selectedTier === 'agency' ? { scale: 1.05 } : { scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 10,
                  duration: 0.3
                }}
              >
                <div
                  className="relative rounded-xl p-12 border-2 transition-all duration-300 ease-in-out min-h-[280px] flex flex-col items-center justify-center text-center"
                  style={{
                    background: 'linear-gradient(215deg, #F7F7F7 35%, #CED6EF 100%)',
                    borderColor: selectedTier === 'agency' ? '#290BD9' : 'transparent'
                  }}
                >
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                    <Users className="h-7 w-7" style={{ color: '#8B9DC3' }} />
                  </div>
                  <h3 className="text-2xl font-medium mb-2" style={{ color: '#3D4F6B' }}>Agency</h3>
                  <p className="text-lg" style={{ color: '#4D5F7B' }}>Manage multiple client campaigns</p>
                  <div className="absolute top-6 right-6 w-7 h-7 rounded-full bg-white border-2 flex items-center justify-center transition-all duration-300" style={{ borderColor: selectedTier === 'agency' ? '#10B981' : '#E5E7EB' }}>
                    <Check
                      className="h-5 w-5 transition-opacity duration-200"
                      strokeWidth={3}
                      style={{
                        color: '#10B981',
                        opacity: selectedTier === 'agency' ? 1 : 0
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            </label>

            {/* Enterprise Card */}
            <label className="cursor-pointer block">
              <input
                type="radio"
                name="tier-selection"
                value="enterprise"
                className="sr-only peer"
                onChange={(e) => setSelectedTier(e.target.value)}
              />
              <motion.div
                animate={selectedTier === 'enterprise' ? { scale: 1.05 } : { scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 10,
                  duration: 0.3
                }}
              >
                <div
                  className="relative rounded-xl p-12 border-2 transition-all duration-300 ease-in-out min-h-[280px] flex flex-col items-center justify-center text-center"
                  style={{
                    background: 'linear-gradient(215deg, #F7F7F7 35%, #E6CDED 100%)',
                    borderColor: selectedTier === 'enterprise' ? '#290BD9' : 'transparent'
                  }}
                >
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                    <Award className="h-7 w-7" style={{ color: '#B694BC' }} />
                  </div>
                  <h3 className="text-2xl font-medium mb-2" style={{ color: '#533D5A' }}>Enterprise</h3>
                  <p className="text-lg" style={{ color: '#634D6A' }}>Large-scale advertising solutions</p>
                  <div className="absolute top-6 right-6 w-7 h-7 rounded-full bg-white border-2 flex items-center justify-center transition-all duration-300" style={{ borderColor: selectedTier === 'enterprise' ? '#10B981' : '#E5E7EB' }}>
                    <Check
                      className="h-5 w-5 transition-opacity duration-200"
                      strokeWidth={3}
                      style={{
                        color: '#10B981',
                        opacity: selectedTier === 'enterprise' ? 1 : 0
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            </label>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Spring Animation Pattern</h2>
          <p className="text-sm text-gray-700 mb-4">
            The wizard cards above use Framer Motion spring animation for selection feedback. This creates a smooth, bouncy scale effect that's more engaging than CSS transitions.
          </p>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Implementation:</h3>
            <pre className="text-xs text-gray-900 overflow-x-auto">
{`<motion.div
  animate={isSelected ? { scale: 1.05 } : { scale: 1 }}
  transition={{
    type: "spring",
    stiffness: 400,
    damping: 10,
    duration: 0.3
  }}
>
  {/* Card content */}
</motion.div>`}
            </pre>
          </div>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-l-blue-400">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Parameters:</h3>
            <ul className="text-sm text-gray-900 space-y-1 list-disc list-inside">
              <li><strong>stiffness: 400</strong> - Higher values create a stiffer spring (faster oscillation)</li>
              <li><strong>damping: 10</strong> - Lower values create more bounce (less damping)</li>
              <li><strong>duration: 0.3</strong> - Base duration in seconds</li>
              <li><strong>scale: 1.05</strong> - 5% scale increase when selected</li>
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Wizard Buttons</h2>
          <p className="text-sm text-gray-700 mb-6">
            Standard button patterns for wizard navigation. Primary action buttons always use Cobalt brand color.
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3">Primary Action Button (Continue/Create)</h3>
              <div className="flex justify-center p-8 bg-gray-50 rounded-lg">
                <Button
                  size="lg"
                  className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <span>CONTINUE</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <pre className="text-xs text-gray-900 overflow-x-auto">
{`<Button
  size="lg"
  className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5]
    text-white shadow-lg disabled:opacity-50
    disabled:cursor-not-allowed flex items-center
    justify-center space-x-2"
>
  <span>CONTINUE</span>
  <ArrowRight className="h-4 w-4" />
</Button>`}
                </pre>
              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3">Back Button</h3>
              <div className="flex justify-center p-8 bg-gray-50 rounded-lg">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>BACK</span>
                </Button>
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <pre className="text-xs text-gray-900 overflow-x-auto">
{`<Button
  variant="outline"
  size="lg"
  className="w-[216px] py-6 flex items-center
    justify-center space-x-2 text-gray-700
    hover:text-gray-900 border-gray-200
    bg-white hover:bg-gray-50"
>
  <ArrowLeft className="h-4 w-4" />
  <span>BACK</span>
</Button>`}
                </pre>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-l-blue-400">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Button Guidelines:</h3>
            <ul className="text-sm text-gray-900 space-y-1 list-disc list-inside">
              <li><strong>Primary (Continue/Create):</strong> Always use Cobalt #290BD9, width 280px</li>
              <li><strong>Back:</strong> Always outline variant, width 216px</li>
              <li><strong>Height:</strong> All wizard buttons use py-6 for consistent large height</li>
              <li><strong>Icons:</strong> ArrowRight for forward actions, ArrowLeft for back</li>
              <li><strong>Text:</strong> UPPERCASE for primary actions</li>
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Progress Indicators</h2>
          <p className="text-sm text-gray-700 mb-6">
            Dots showing current step in the wizard flow. Active step uses Cobalt brand color.
          </p>

          <div className="flex justify-center p-8 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center gap-3">
              <div className="w-3 h-3 rounded-full bg-[#290BD9]"></div>
              <div className="w-3 h-3 rounded-full bg-[#290BD9]"></div>
              <div className="w-3 h-3 rounded-full bg-[#290BD9]"></div>
              <div className="w-3 h-3 rounded-full bg-white border border-gray-300"></div>
              <div className="w-3 h-3 rounded-full bg-white border border-gray-300"></div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Implementation:</h3>
            <pre className="text-xs text-gray-900 overflow-x-auto">
{`<div className="flex items-center justify-center gap-3">
  {/* Completed/Active steps */}
  <div className="w-3 h-3 rounded-full bg-[#290BD9]"></div>

  {/* Inactive steps */}
  <div className="w-3 h-3 rounded-full bg-white border border-gray-300"></div>
</div>`}
            </pre>
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-l-blue-400">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Guidelines:</h3>
            <ul className="text-sm text-gray-900 space-y-1 list-disc list-inside">
              <li><strong>Active/Completed:</strong> Filled with Cobalt #290BD9</li>
              <li><strong>Inactive:</strong> White background with gray border</li>
              <li><strong>Size:</strong> w-3 h-3 (12px diameter)</li>
              <li><strong>Spacing:</strong> gap-3 between dots</li>
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Wizard Form Card Pattern</h2>
          <p className="text-sm text-gray-700 mb-6">
            Use this pattern for wizard steps that require form input. The card provides standard styling with centered, well-padded content.
          </p>

          <div className="flex justify-center">
            <div className="w-[75%]">
              <Card className="rounded-lg border bg-white">
                <CardContent className="pt-6 space-y-6">
                  <div className="p-6">
                    <div className="max-w-md mx-auto space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="wizard-demo-input" className="text-base font-medium text-gray-900">
                          Input Label
                        </Label>
                        <Input
                          id="wizard-demo-input"
                          type="text"
                          placeholder="e.g. Example Input"
                          className="h-[54px] w-full rounded-md border border-input bg-transparent px-3 shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-base"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between items-center pt-6">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>BACK</span>
                </Button>

                <Button
                  size="lg"
                  className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <span>CONTINUE</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Pattern Structure:</h3>
            <pre className="text-xs text-gray-900 overflow-x-auto">
{`<div className="flex justify-center">
  <div className="w-[75%]">
    <Card className="rounded-lg border bg-white">
      <CardContent className="pt-6 space-y-6">
        <div className="p-6">
          <div className="max-w-md mx-auto space-y-4">
            {/* Form content */}
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="flex justify-between items-center pt-6">
      <Button variant="outline" size="lg" className="w-[216px] py-6...">
        <ArrowLeft className="h-4 w-4" />
        <span>BACK</span>
      </Button>

      <Button size="lg" className="w-[280px] py-6 bg-[#290BD9]...">
        <span>CONTINUE</span>
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  </div>
</div>`}
            </pre>
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-l-blue-400">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Key Features:</h3>
            <ul className="text-sm text-gray-900 space-y-1 list-disc list-inside">
              <li><strong>Card wrapper:</strong> Standard card classes (rounded-lg border bg-white shadow-sm)</li>
              <li><strong>Double padding:</strong> CardContent with pt-6 + inner div with p-6 for generous spacing</li>
              <li><strong>Centered content:</strong> max-w-md mx-auto constrains and centers form</li>
              <li><strong>Large input:</strong> h-[54px] for prominent, accessible form fields</li>
              <li><strong>Required buttons:</strong> Back (outline, 216px) and Continue (Cobalt, 280px) always below card</li>
              <li><strong>75% width container:</strong> Outer wrapper constrains card width to 75% of available space</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
